<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <vue-resizable
    width="100%"
    :min-height="145"
    height="145px"
    :active="['b']"
    class="rich-text-component"
    :class="{ disabled: disabled, focused: focused }"
  >
    <div class="rich-text-editor">
      <textarea
        :id="name"
        v-model="tempValue"
        :name="name"
        :placeholder="placeholder"
        class="form-control"
        rows="5"
        :required="required"
        hidden
      />

      <!-- eslint-disable-next-line vue/v-bind-style -->
      <div class="rich-text__wrapper" v-bind:class="{ textareaInvalid: isTextareaInvalid }">
        <div class="rich-text__toolbar">
          <span class="ql-formats">
            <select v-if="sizeSelection" class="ql-header">
              <option value="1" />
              <option value="2" />
              <option value="3" />
              <option selected />
            </select>
            <button title="Bold" class="ql-bold" />
            <button title="Italic" class="ql-italic" />
            <button title="Underline" class="ql-underline" />
            <button title="Strikethrough" class="ql-strike" />
            <button title="Link" class="ql-link" />
          </span>
          <span class="ql-formats">
            <button title="List" class="ql-list" value="ordered" />
            <button title="Bullet" class="ql-list" value="bullet" />
          </span>
        </div>
        <div
          :class="['rich-text__content', 'ql-container', 'ql-snow', validationClass, { 'fixed-height': fixedHeight }]"
        />
      </div>
    </div>
  </vue-resizable>
</template>

<script>
import $ from 'jquery';
import Quill from 'quill';
import VueResizable from 'vue-resizable';

const Link = Quill.import('formats/link');

export default {
  name: 'RichText',
  components: { VueResizable },
  props: {
    name: {
      type: [String, Number],
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    validationClass: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    required: {
      type: String,
      default: null,
    },
    sizeSelection: {
      type: Boolean,
      default: false,
    },
    fixedHeight: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    textToInsert: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const formats = (this.sizeSelection ? ['header'] : []).concat([
      'bold',
      'italic',
      'link',
      'underline',
      'list',
      'bullet',
      'strike',
    ]);

    return {
      formats,
      tempValue: this.value || '',
      isTextareaInvalid: false,
      focused: false,
    };
  },
  watch: {
    value(text) {
      const space = /[\u200B-\u200D\uFEFF\n]/g;
      const cleanText = $(text)
        .remove('.ql-cursor')
        .text()
        .replace(space, '');
      const currentContent = this.editor.getText().replace(space, '');

      if (cleanText === currentContent) {
        return;
      }

      this.tempValue = text;
      this.editor.pasteHTML(text);
    },
    disabled: {
      handler(value) {
        if (!this.editor) return;
        this.editor.enable(!value);
      },
      immediate: true,
    },
    textToInsert(text) {
      if (!text.value) return;
      let pos = this.editor.getSelection(true);
      if (pos.length) {
        this.editor.deleteText(pos.index, pos.length);
      }
      this.editor.insertText(pos.index, text.value);
      this.editor.setSelection(pos.index, text.value.length);
    },
  },
  mounted() {
    const $el = $(this.$el);
    this.textarea = $el.find('textarea');
    this.$toolbar = $el.find('.rich-text__toolbar');
    this.$wrap = $el.find('.rich-text__content');
    const $scroller = this.fixedHeight ? this.$wrap : $el.closest('.modal, .main-content, body');
    this.$wrap.html(this.textarea.val());
    this.editor = new Quill(this.$wrap[0], {
      bounds: this.$wrap[0],
      theme: 'snow',
      formats: this.formats,
      placeholder: this.textarea.attr('placeholder'),
      scrollingContainer: $scroller[0],
      modules: {
        toolbar: {
          container: this.$toolbar[0],
        },
      },
    });
    Link.sanitize = url => {
      return this.prependToLinkURL(url);
    };
    this.editor.on('text-change', this.onChange);
    this.editor.on('selection-change', this.onSelectionChange);
    this.isTextareaInvalid = false;
    if (this.disabled) {
      this.editor.enable(!this.disabled);
    }
  },
  methods: {
    prependToLinkURL(url) {
      // Regex to check if url contains http:/https:/ftp: prepended
      const urlProtocolPattern = /^((http|https|ftp):\/\/)/;
      return !urlProtocolPattern.test(url) ? `http://${url}` : url;
    },
    onChange() {
      this.$emit('focused', true);
      const content = $.trim(this.editor.root.innerText.replace(/\n/g, ''));
      const html = content.length ? this.editor.root.innerHTML : '';
      this.textarea.val(html);
      this.$emit('input', html);
      this.setTextareaInvalid();
    },
    onSelectionChange(range, oldRange) {
      if (range === null && oldRange !== null) {
        this.$emit('focused', false);
        this.$emit('blur');
      } else if (range !== null && oldRange === null) {
        this.$emit('focused', true);
      }
    },
    setTextareaInvalid() {
      this.isTextareaInvalid = !this.textarea[0].checkValidity();
    },
  },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';
@import '~@/design/colors';
@import '~@/design/variables';
@import '~quill/dist/quill.core.css';
@import '~quill/dist/quill.snow.css';

/* stylelint-disable no-descending-specificity */
.textareaInvalid {
  .rich-text__content,
  .rich-text__toolbar {
    border-right: 1px solid $red;
    border-left: 1px solid $red;
  }
  .rich-text__toolbar {
    border-top: 1px solid $red;
  }
  .rich-text__content {
    border-bottom: 1px solid $red;
  }
}

.resizable-component {
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  &::after {
    content: '||';
    right: 0;
    bottom: 0;
    position: absolute;
    height: 10px;
    width: 10px;
    color: $color-oslo-gray;
    transform: rotate(45deg) translate(3px, -5px);
  }

  &.disabled {
    opacity: 0.6;
  }
}

.rich-text-editor {
  height: 100%;
}

.rich-text {
  &__wrapper {
    border: 0;
    height: 100%;
    display: flex;
    flex-direction: column;

    &.disabled {
      &,
      * {
        cursor: not-allowed !important;
      }

      .ql-container {
        background: $color-athens-gray;
      }
    }
  }

  &__toolbar.ql-snow {
    background: $color-white;
    padding: 3px !important;
    position: sticky;
    z-index: 2;
    top: 0;
    border-radius: 2px 2px 0 0;

    button:disabled {
      pointer-events: none;
      opacity: 0.3;
    }
  }

  &__content {
    min-height: 100px;
    flex: 1 1 auto;
    background: $color-white;
    height: auto;
    overflow: auto;
    border-top: 0 !important;
    border-radius: 0 0 2px 2px;

    &.fixed-height {
      max-height: 250px;
    }
    &.is-invalid {
      border-color: $color-error;
    }

    .ql-editor {
      height: calc(100% - 33px);
      min-height: 100px;
      padding: 8px 10px;
      overflow-y: visible;

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin-bottom: $headings-margin-bottom;
      }

      p,
      ol,
      pre,
      blockquote {
        margin-bottom: $paragraph-margin-bottom;
      }

      &.ql-blank::before {
        font-style: normal;
        left: 10px;
      }
    }

    .ql-editing:not(.ql-hidden) {
      display: flex;
      visibility: visible;
    }
  }
}
</style>
